//
// Gremlin traversal support.
//

// import { Iri } from './rec-cache-types'

// For anonymous traversals.
const __ = {
  out: (edgeType) => (traversal) => traversal.out(edgeType),
  in: (edgeType) => (traversal) => traversal.in(edgeType),
  union:
    (...fns) =>
    (traversal) =>
      traversal.union(...fns),
  has:
    (property, ...args) =>
    (traversal) =>
      traversal.has(property, ...args),
  hasType: (type) => (traversal) => traversal.hasType(type),
  hasLabel: (label) => (traversal) => traversal.hasType(label),
}

class Traverser {
  constructor(node, path = null) {
    this.node = node
    this.path = path || [node]
  }
  extend(node) {
    return new Traverser(node, this.path.concat(node))
  }
  clone() {
    return new Traverser(this.node, [...this.path])
  }
}

class Traversal {
  constructor(nodes, edges, traversers = null) {
    this.nodes = nodes
    this.edges = edges
    this.traversers = traversers || nodes.map((node) => new Traverser(node))
  }

  clone() {
    return new Traversal(
      this.nodes,
      this.edges,
      this.traversers.map((traverser) => traverser.clone())
    )
  }

  // Filters the traversers (nodes) if ids are set. Otherwise return all.
  // Asu such may be omitted since the constructor does this but included for completeness.
  // In gremlin it may be used in the middle of a query. Not supported yet.
  // Each parameter is an Iri
  V(...ids) {
    if (ids.length) {
      this.traversers = ids
        .map((id) =>
          this.traversers.find((traverser) => traverser.node.id.equals(id))
        )
        .filter((traverser) => !!traverser)
    }
    return this
  }

  // This is a Gremlin has step filtering/removing traversers.
  // Parameter property is a String or an Iri and args are Value.
  has(property, ...args) {
    if (property == 'type') {
      // Gremlin hasLabel(labels... ), note logical or if several labels are given.
      // Note label is substituted with type in this implementation.
      // Also note the list of arguments (types) are compared to the individual / node type, not yhe lisrt of types on the individual.
      // Ie no support for multi inheritance.
      this.traversers = this.traversers.filter((traverser) =>
        args.find((a) => a.equals(traverser.node.type))
      )
    } else {
      if (args.length == 1) {
        // Gremlin has(key, value).
        const [arg] = args
        // TODO consider if faster to manipulate on the Property itself... may require a getbyname on the individual.
        this.traversers = this.traversers.filter((traverser) =>
          traverser.node.getValuesByName(property).find((v) => v.equals(arg))
        )
      } else {
        // Gremlin has(key, predicate).
        // TODO fix as needed.
      }
    }
    return this
  }

  // Convenience. Parameter type is an Iri.
  hasType(type) {
    return this.has('type', type)
  }

  // Convenience to establish parity with the Gremlin has step.
  // Parameter label is an iri.
  hasLabel(label) {
    return this.has('type', label)
  }

  out(edgeType = null) {
    console.log('OUT', edgeType)

    this.traversers = this.traversers.flatMap((traverser) =>
      this.edges
        .filter(
          (edge) =>
            edge.source.equals(traverser.node.id) &&
            (!edgeType || edge.label.equals(edgeType))
        )
        .map((edge) => this.nodes.find((node) => node.id.equals(edge.target)))
        .filter((node) => !!node)
        .map((target) => traverser.extend(target))
    )

    return this
  }

  in(edgeType = null) {
    console.log('IN', edgeType)

    this.traversers = this.traversers.flatMap((traverser) =>
      this.edges
        .filter(
          (edge) =>
            edge.target.equals(traverser.node.id) &&
            (!edgeType || edge.label.equals(edgeType))
        )
        .map((edge) => this.nodes.find((node) => node.id.equals(edge.source)))
        .filter((node) => !!node)
        .map((target) => traverser.extend(target))
    )

    return this
  }

  // Clones the traversal for each of the functions and then aggregate results back into the traversal.
  union(...traversalFns) {
    console.log('UNION', traversalFns)

    this.traversers = traversalFns.flatMap((fn) => fn(this.clone()).traversers)

    return this
  }

  // The Gremlin repeat step.
  // Note simulates without the emit-step. If such was to be set in front all iteraitons should emit.
  // TODO potentially add as a flag or a futere step...
  repeat(traversalFn) {
    console.log('REPEAT', traversalFn)

    let frontier = this.traversers.map((traverser) => traverser.clone())
    // TODO rename to finalTraversers?
    const newTraversers = []

    while (frontier.length) {
      const traversal = new Traversal(this.nodes, this.edges, frontier)
      const nextTraversers = traversalFn(traversal).traversers

      if (nextTraversers.length == 0) {
        // Only output terminal paths.
        newTraversers.push(...frontier)
      }

      frontier = nextTraversers
    }

    this.traversers = newTraversers
    return this
  }

  // This is a Gremlin final step terminating the traversing.
  // Returns array of nodes.
  toList() {
    return [...this.traversers.map((traverser) => traverser.node)] // Returns a copy of the final node list
  }

  // Simulates the Gremlin path step in returning an array of path arrays.
  // In Gremlin the path step manipulates the traversal object which is not inmplemented.
  path() {
    return [...this.traversers.map((traverser) => traverser.path)]
  }
}

export { Traversal, __ }
